import React, { useState } from "react";
import { inputClass } from "@/lib/formStyles";
import AvailabilityFormShell from "./AvailabilityFormShell";
import { ABSENCE_REASON_MAX_LENGTH, Absence, AbsenceRequest } from "../../services/availability/types";
import { isDateRangeValid } from "../../utils/availability";
import { todayIsoDate } from "../../utils/age";

type AbsenceFormModalProps = {
  /** Absence modifiée ; absente = création. */
  absence: Absence | null;
  isSaving: boolean;
  onSubmit: (request: AbsenceRequest) => void;
  onDelete: (absence: Absence) => void;
  onClose: () => void;
};

/** Ajout / modification d'une absence (journées entières, du … au … inclus). */
const AbsenceFormModal: React.FC<AbsenceFormModalProps> = ({ absence, isSaving, onSubmit, onDelete, onClose }) => {
  const [startDate, setStartDate] = useState(absence?.startDate ?? todayIsoDate());
  const [endDate, setEndDate] = useState(absence?.endDate ?? todayIsoDate());
  const [reason, setReason] = useState(absence?.reason ?? "");

  const isValid = isDateRangeValid(startDate, endDate);

  const changeStartDate = (value: string) => {
    setStartDate(value);
    // Une absence d'un jour se saisit en un geste : la fin suit le début.
    if (value && (!endDate || endDate < value)) setEndDate(value);
  };

  return (
    <AvailabilityFormShell
      eyebrow="Absence"
      title={absence ? "Modifier l'absence" : "Nouvelle absence"}
      submitLabel={absence ? "Enregistrer" : "Ajouter l'absence"}
      canSubmit={isValid}
      isSaving={isSaving}
      onSubmit={() => onSubmit({ startDate, endDate, reason: reason.trim() || null })}
      onClose={onClose}
      onDelete={absence ? () => onDelete(absence) : undefined}
      deleteLabel="Supprimer cette absence"
    >
      <div className="grid grid-cols-2 gap-4">
        <label className="block text-sm font-medium text-slate-700">
          Du
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(event) => changeStartDate(event.target.value)}
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Au (inclus)
          <input
            type="date"
            className={inputClass}
            value={endDate}
            min={startDate || undefined}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
      </div>
      {!isValid && <p className="text-xs text-rose-600">La date de fin ne peut pas précéder la date de début.</p>}
      <label className="block text-sm font-medium text-slate-700">
        Motif <span className="text-xs font-normal text-slate-400">(facultatif)</span>
        <input
          type="text"
          className={inputClass}
          value={reason}
          maxLength={ABSENCE_REASON_MAX_LENGTH}
          placeholder="Ex. Congrès, congé"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </AvailabilityFormShell>
  );
};

export default AbsenceFormModal;
